import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import {
  UntypedFormGroup,
  UntypedFormControl,
  Validators,
  ValidatorFn,
} from "@angular/forms";
import { Subject, Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { Branch } from "../../branch-settings/branch-settings.interface";
import { BRANCH_COUNTRY_VARIABLE_NAME } from "../../branch-settings/branch-predefined-variables";
import { BranchSettingsService } from "../../branch-settings/branch-settings.service";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../error-state-matcher";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "app-employee-branch-section",
  templateUrl: "./employee-branch-section.component.html",
  styleUrls: ["./employee-branch-section.component.scss"],
  standalone: false,
})
export class EmployeeBranchSectionComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() branchId: string | null = null;
  @Input() employeeBranchVariables: Record<string, unknown> = {};
  @Input() errorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();
  @Output() branchChange = new EventEmitter<{
    branchId: string | null;
    branchVariables: Record<string, unknown>;
  }>();

  branches: Branch[] = [];
  readonly branchLabel = (branch: Branch) => branch.name || "";
  readonly branchValue = (branch: Branch) => branch.branchId;
  selectedBranch: Branch | null = null;
  branchVariablesForm: UntypedFormGroup;

  private readonly destroy$ = new Subject<void>();
  private formChangesSub?: Subscription;
  private isInitializing = false;
  private lastSyncedBranchId: string | null | undefined = undefined;
  private lastSyncedVariablesKey = "";

  constructor(
    private branchSettingsService: BranchSettingsService,
    private translate: TranslateService,
  ) {
    this.branchVariablesForm = new UntypedFormGroup({});
  }

  ngOnInit(): void {
    this.loadBranches();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.branches.length) {
      return;
    }

    if (changes["branchId"]) {
      this.updateSelectedBranch();
      return;
    }

    if (changes["employeeBranchVariables"] && this.selectedBranch) {
      const nextKey = this.variablesKey(this.employeeBranchVariables);
      if (nextKey === this.lastSyncedVariablesKey) {
        return;
      }
      this.initializeBranchVariables();
    }
  }

  ngOnDestroy(): void {
    this.formChangesSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadBranches(): void {
    this.branchSettingsService
      .fetchBranches()
      .pipe(takeUntil(this.destroy$))
      .subscribe((branches) => {
        this.branches = branches;
        this.updateSelectedBranch();
      });
  }

  updateSelectedBranch(): void {
    if (this.branchId) {
      this.selectedBranch =
        this.branches.find((b) => b.branchId === this.branchId) || null;
      if (this.selectedBranch) {
        if (this.lastSyncedBranchId === this.branchId) {
          return;
        }
        this.initializeBranchVariables();
      } else {
        this.lastSyncedBranchId = this.branchId;
        this.clearBranchVariablesForm();
      }
    } else {
      this.selectedBranch = null;
      this.lastSyncedBranchId = null;
      this.lastSyncedVariablesKey = "";
      this.clearBranchVariablesForm();
    }
  }

  onBranchChange(branchId: string | null): void {
    this.branchId = branchId;
    this.selectedBranch = branchId
      ? this.branches.find((b) => b.branchId === branchId) || null
      : null;

    this.clearBranchVariablesForm();
    this.lastSyncedBranchId = undefined;
    this.lastSyncedVariablesKey = "";

    if (this.selectedBranch) {
      this.initializeBranchVariables(true);
    }

    this.emitChanges();
  }

  private clearBranchVariablesForm(): void {
    this.formChangesSub?.unsubscribe();
    this.formChangesSub = undefined;
    Object.keys(this.branchVariablesForm.controls).forEach((key) => {
      this.branchVariablesForm.removeControl(key, { emitEvent: false });
    });
  }

  private initializeBranchVariables(useBranchDefaults = false): void {
    if (!this.selectedBranch || !this.selectedBranch.variables) {
      return;
    }

    this.isInitializing = true;
    this.clearBranchVariablesForm();

    const branchVars = this.selectedBranch.variables;
    const employeeBranchVars = useBranchDefaults
      ? {}
      : this.employeeBranchVariables || {};

    Object.keys(branchVars).forEach((varName) => {
      const branchVar = branchVars[varName];
      const isOverridable = branchVar.overridable !== false;
      const employeeValue =
        employeeBranchVars[varName] || branchVar.defaultValue || "";

      const control = new UntypedFormControl({
        value: employeeValue,
        disabled: !isOverridable,
      });

      if (branchVar.mandatory && isOverridable) {
        const requiredValidator: ValidatorFn = Validators.required.bind(
          null,
        ) as ValidatorFn;
        control.setValidators(requiredValidator);
      }

      this.branchVariablesForm.addControl(varName, control, {
        emitEvent: false,
      });
    });

    this.lastSyncedBranchId = this.branchId;
    this.lastSyncedVariablesKey = this.variablesKey(
      this.collectBranchVariables(),
    );

    this.formChangesSub = this.branchVariablesForm.valueChanges.subscribe(
      () => {
        if (this.isInitializing) {
          return;
        }
        this.emitChanges();
      },
    );

    this.isInitializing = false;
  }

  private collectBranchVariables(): Record<string, unknown> {
    const branchVariables: Record<string, unknown> = {};

    if (this.selectedBranch && this.branchVariablesForm) {
      Object.keys(this.branchVariablesForm.controls).forEach((key) => {
        const control = this.branchVariablesForm.get(key);
        if (
          control &&
          !control.disabled &&
          control.value !== undefined &&
          this.selectedBranch
        ) {
          const branchVar = this.selectedBranch.variables[key];
          const defaultValue = branchVar?.defaultValue || "";
          if (
            control.value !== defaultValue ||
            this.employeeBranchVariables?.[key]
          ) {
            branchVariables[key] = control.value;
          }
        }
      });
    }

    return branchVariables;
  }

  private emitChanges(): void {
    const branchVariables = this.collectBranchVariables();
    this.lastSyncedVariablesKey = this.variablesKey(branchVariables);
    this.branchChange.emit({
      branchId: this.branchId,
      branchVariables:
        Object.keys(branchVariables).length > 0 ? branchVariables : {},
    });
  }

  private variablesKey(
    vars: Record<string, unknown> | null | undefined,
  ): string {
    const source = vars || {};
    return Object.keys(source)
      .sort()
      .map((key) => `${key}:${JSON.stringify(source[key] ?? "")}`)
      .join("|");
  }

  getBranchVariableNames(): string[] {
    return this.selectedBranch && this.selectedBranch.variables
      ? Object.keys(this.selectedBranch.variables)
      : [];
  }

  getBranchVariableLabel(varName: string): string {
    if (!this.selectedBranch || !this.selectedBranch.variables) {
      return varName;
    }
    const branchVar = this.selectedBranch.variables[varName];
    if (branchVar?.label) {
      return branchVar.label;
    }
    const defaultTranslationKey = `branchSettings.variableLabels.${varName}`;
    const translated = this.translate.instant(defaultTranslationKey) as string;
    if (translated !== defaultTranslationKey) {
      return defaultTranslationKey;
    }
    return varName;
  }

  isBranchVariableOverridable(varName: string): boolean {
    if (!this.selectedBranch || !this.selectedBranch.variables) {
      return false;
    }
    const branchVar = this.selectedBranch.variables[varName];
    return branchVar?.overridable !== false;
  }

  isBranchCountryVariable(varName: string): boolean {
    return varName === BRANCH_COUNTRY_VARIABLE_NAME;
  }

  get branchVariablesFormValid(): boolean {
    return this.branchVariablesForm.valid;
  }
}
